import React, { memo } from "react";
import Animated, {
  FadeInUp,
  FadeOutDown,
  ReduceMotion,
} from "react-native-reanimated";

import SkeletonPlaceholder from "@/components/molecules/SkeletonPlaceholder";

import { Box } from "@/components/atoms/Box";
import { Heading } from "@/components/atoms/Heading";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";

const AnimatedView =
  (Animated as typeof Animated & { View: typeof Animated.View }).View || Box;

const skeletonMetricsChart = [
  "Carrying",
  "Dribbling",
  "Passing",
  "Positioning",
  "Shot Stopping",
  "Passing",
  "Shooting",
];
/**
 *
 */
interface Props {
  skeletonMetrics: (boolean[] | null)[][];
  /**
   * Whether dark theme is active
   */
  isDark: boolean;
  /**
   * Whether device has small screen
   */
  isSmallScreen: boolean;
}
/**
 * Skeleton loading placeholder for player comparison view with metrics and charts
 * @param root0 - The component props
 * @param root0.skeletonMetrics - 2D array of boolean flags indicating which metric skeleton placeholders to show
 * @param root0.isDark - Whether dark theme is active for styling skeleton background colors
 * @param root0.isSmallScreen - Whether device has small screen for responsive skeleton layout
 */
const SkeletonComparePlayerView = ({
  skeletonMetrics,
  isDark,
  isSmallScreen,
}: Props) => (
  <AnimatedView
    entering={FadeInUp.duration(500).reduceMotion(ReduceMotion.System)}
    exiting={FadeOutDown.duration(500).reduceMotion(ReduceMotion.System)}
  >
    <ScrollView>
      <SkeletonPlaceholder>
        <ScrollView contentContainerClassName="gap-6 pb-10">
          <Stack direction="horizontal" className="items-center gap-4">
            <Box
              className={`h-12 flex-1 ${isDark ? "bg-surface-strong" : "bg-surface-muted"}`}
            />
            <Box
              className={`h-12 flex-1 ${isDark ? "bg-surface-strong" : "bg-surface-muted"}`}
            />
          </Stack>
          <Stack className="gap-4 pt-4">
            {skeletonMetrics.map((metric, i) => (
              <Stack
                direction="horizontal"
                key={`player_detail_${i.toString()}`}
                className="flex-1 justify-between gap-4"
              >
                {metric.map(
                  (item, idx) =>
                    item && (
                      <Box className="flex-1" key={`metric_${idx.toString()}`}>
                        <Stack
                          className={`flex-1 gap-1 rounded-none px-3 pb-3 pt-2 ${!isSmallScreen ? "border border-surface-muted" : "border-2 border-surface-muted"}`}
                        >
                          <Box
                            className={`h-8 w-full ${isDark ? "bg-surface-strong" : "bg-surface-muted"}`}
                          />
                          <Box
                            className={`h-6 w-full ${isDark ? "bg-surface-strong" : "bg-surface-muted"}`}
                          />
                        </Stack>
                      </Box>
                    )
                )}
              </Stack>
            ))}
          </Stack>
          {skeletonMetricsChart.map((item, idx) => (
            <Stack
              key={`chart_${idx.toString()}`}
              className={`gap-4 rounded-none p-4 ${!isSmallScreen ? "border border-surface-muted" : "border-2 border-background-gray"}`}
            >
              <Heading variant="title">{item}</Heading>
              <Box
                className={`h-96 ${isDark ? "bg-surface-strong" : "bg-surface-muted"}`}
              />
              <Stack
                direction="horizontal"
                className="flex-1 items-center gap-4"
              >
                <Box
                  className={`h-8 flex-1 ${isDark ? "bg-surface-strong" : "bg-surface-muted"}`}
                />
                <Box
                  className={`h-8 flex-1 ${isDark ? "bg-surface-strong" : "bg-surface-muted"}`}
                />
              </Stack>
            </Stack>
          ))}
        </ScrollView>
      </SkeletonPlaceholder>
    </ScrollView>
  </AnimatedView>
);

SkeletonComparePlayerView.displayName = "SkeletonComparePlayerView";

export default memo(SkeletonComparePlayerView);
